import Link from "next/link";
import { Calendar, ChevronRight, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import Footer from "@/components/shared/Footer";

type LegalSection = {
  title: string;
  content: string | string[];
  subSections?: LegalSection[];
};

type LegalData = {
  title: string;
  description: string;
  effectiveDate?: string;
  sections: LegalSection[];
};

interface LegalLayoutProps {
  content: LegalData;
  /** Short label shown in the breadcrumb / hero eyebrow. Defaults to the title. */
  eyebrow?: string;
  /** Optional "last updated" date shown as a chip in the hero. */
  lastUpdated?: string;
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Drop a leading "1. " / "2. " style number so the auto "Section NN" label owns the numbering. */
const stripLeadingNumber = (s: string) => s.replace(/^\d+\.\s*/, "");

/**
 * Parses inline <Link className="..." href="...">text</Link> markup that lives inside the
 * content strings and renders real, brand-styled links. Plain text passes straight through.
 */
const parseContent = (content: string) => {
  const linkRegex = /<Link className="([^"]+)" href="([^"]+)">([^<]+)<\/Link>/g;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    const [fullMatch, , href, text] = match;
    const index = match.index;

    if (index > lastIndex) {
      parts.push(content.substring(lastIndex, index));
    }

    parts.push(
      <Link
        key={index}
        href={href}
        prefetch={false}
        className="font-medium text-crayoBlue underline decoration-crayoBlue/30 underline-offset-[3px] transition-colors hover:decoration-crayoBlue"
      >
        {text}
      </Link>,
    );

    lastIndex = index + fullMatch.length;
  }

  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts;
};

/** Renders a section's body, paragraph or bulleted list, plus any nested sub-sections. */
const SectionBody = ({ section }: { section: LegalSection }) => {
  const isList = Array.isArray(section.content);
  const hasText = isList
    ? (section.content as string[]).length > 0
    : (section.content as string).trim().length > 0;

  return (
    <div className="space-y-4">
      {hasText &&
        (isList ? (
          <ul className="space-y-2.5">
            {(section.content as string[]).map((item, i) => (
              <li
                key={i}
                className="relative pl-5 before:absolute before:left-0 before:top-[0.7em] before:size-1.5 before:rounded-full before:bg-crayoBlue/70"
              >
                {parseContent(item.replace(/^[•\-]\s*/, ""))}
              </li>
            ))}
          </ul>
        ) : (
          <p>{parseContent(section.content as string)}</p>
        ))}

      {section.subSections?.map((sub, i) => (
        <div key={i} className="space-y-2">
          {sub.title ? (
            <h3 className="text-[15px] font-semibold text-gray-900 sm:text-[15.5px]">
              {sub.title}
            </h3>
          ) : null}
          <SectionBody section={sub} />
        </div>
      ))}
    </div>
  );
};

export function LegalLayout({ content, eyebrow, lastUpdated }: LegalLayoutProps) {
  const { title, effectiveDate, sections } = content;
  const crumb = eyebrow ?? title;

  const slugs = useMemo(() => sections.map((s) => slugify(s.title)), [sections]);
  const [activeSlug, setActiveSlug] = useState<string | null>(slugs[0] ?? null);

  // Scroll-spy: highlight the table-of-contents entry for the section currently in view.
  useEffect(() => {
    if (slugs.length === 0) return;
    const OFFSET = 140;

    let rafId = 0;
    let ticking = false;

    const compute = () => {
      let current = slugs[0];
      for (const slug of slugs) {
        const el = document.getElementById(slug);
        if (!el) continue;
        if (el.getBoundingClientRect().top - OFFSET <= 0) {
          current = slug;
        } else {
          break;
        }
      }
      setActiveSlug(current);
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      rafId = requestAnimationFrame(compute);
      ticking = true;
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, [slugs]);

  return (
    <article className="w-full overflow-x-clip bg-white">
      <section className="border-b border-[#E7ECF6] bg-white">
        <div className="mx-auto w-full max-w-screen-xl px-5 pb-12 pt-12 sm:px-6 md:px-12 md:pb-16 md:pt-16 lg:px-20">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 flex items-center gap-1.5 text-[12px] text-[#8B93A7]"
          >
            <Link href="https://crayo.ai" className="transition-colors hover:text-crayoBlue">
              Home
            </Link>
            <ChevronRight className="size-3" strokeWidth={2.5} aria-hidden />
            <Link href="/legal" className="transition-colors hover:text-crayoBlue">
              Legal
            </Link>
            <ChevronRight className="size-3" strokeWidth={2.5} aria-hidden />
            <span className="font-medium text-crayoBlue">{crumb}</span>
          </nav>

          <div className="flex max-w-[820px] flex-col items-start gap-5">
            <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl lg:text-[56px] lg:leading-[1.05]">
              {title}
            </h1>
            {(effectiveDate || lastUpdated) && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px] text-[#5C6678]">
                {effectiveDate && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="size-3.5 text-crayoBlue" strokeWidth={2.25} />
                    Effective {effectiveDate}
                  </span>
                )}
                {lastUpdated && (
                  <span className="inline-flex items-center gap-1.5">
                    <RefreshCw className="size-3.5 text-crayoBlue" strokeWidth={2.25} />
                    Last updated {lastUpdated}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto w-full max-w-screen-xl px-5 py-12 sm:px-6 md:px-12 md:py-16 lg:px-20 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[240px_1fr] lg:gap-16">
            <aside className="hidden lg:block">
              <div className="sticky top-24 flex flex-col gap-3">
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]">
                  On this page
                </span>
                <nav className="relative flex flex-col">
                  <span aria-hidden className="absolute bottom-0 left-0 top-0 w-px bg-[#E7ECF6]" />
                  {sections.map((section, i) => {
                    const slug = slugs[i];
                    const isActive = activeSlug === slug;
                    return (
                      <a
                        key={slug}
                        href={`#${slug}`}
                        className={cn(
                          "relative py-2 pl-4 pr-2 text-[13px] leading-snug transition-colors",
                          isActive
                            ? "font-semibold text-crayoBlue"
                            : "text-[#5C6678] hover:text-crayoBlue",
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            "absolute bottom-1 left-0 top-1 w-[2px] rounded-full bg-crayoBlue transition-opacity duration-200",
                            isActive ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {stripLeadingNumber(section.title)}
                      </a>
                    );
                  })}
                </nav>
              </div>
            </aside>

            <div
              className={cn(
                "flex min-w-0 max-w-[760px] flex-col text-[15px] leading-[1.7] text-[#41485A] sm:text-[16px]",
                "[&_strong]:font-semibold [&_strong]:text-gray-900",
              )}
            >
              {sections.map((section, i) => {
                const slug = slugs[i];
                return (
                  <section
                    key={slug}
                    id={slug}
                    className="scroll-mt-28 border-b border-[#E7ECF6] py-8 first:pt-0 last:border-b-0 last:pb-0"
                  >
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-crayoBlue">
                      Section {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="mt-2 text-[22px] font-semibold leading-[1.2] tracking-tight text-gray-900 sm:text-[26px] lg:text-[28px]">
                      {stripLeadingNumber(section.title)}
                    </h2>
                    <div className="mt-5">
                      <SectionBody section={section} />
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </article>
  );
}

export default LegalLayout;
